import { IPrice, IPurchaseDomainType, ProductCategory } from '@cuspar/ywt.ch.model';
import { PriceGadget, PriceToString } from '../../components/UI/PriceGadget';
import { useCartStore } from '../../state/cartStore';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, cn, Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@cuspar/ui';
import { Check } from 'lucide-react';
import { Link } from 'react-router';
import { ROUTES } from '../../routes';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { i18nDate } from '../../i18n';

/**
 * Other Swiss registrars' .ch prices in CHF cents, VAT included, as published on their own price
 * pages on COMPETITORS_CHECKED_ON. Comparative advertising must stay accurate: re-check them
 * (and the date) whenever ours or theirs change.
 */
const COMPETITORS_CHECKED_ON = new Date('2026-10-24');
const COMPETITORS = [
  { name: 'hosttech', url: 'https://www.hosttech.ch/domain-kaufen/', firstYear: 490, renewal: 1350 },
  { name: 'Hostpoint', url: 'https://www.hostpoint.ch/en/domains/domains.html', firstYear: 500, renewal: 1500 },
  { name: 'NetZone', url: 'https://www.netzone.ch/en/domain-registration', firstYear: 1395, renewal: 1395 },
  { name: 'cyon', url: 'https://www.cyon.ch/domains/domain-kaufen', firstYear: 1490, renewal: 1490 },
];
// Over how many years the totals are compared: long enough for cheap first years to wear off.
const COMPARED_YEARS = 5;

export const DomainPricePage = () => {
  const { t } = useTranslation();
  const prices = useCartStore((state) => state.prices);
  const domainPrice = (tld: string, type: IPurchaseDomainType): IPrice | undefined => prices.find((p) => p.product === ProductCategory.domain && p.tld === tld && p.type === type);
  const tlds = [...new Set(prices.filter((p) => p.product === ProductCategory.domain).map((p) => p.tld!))].sort();
  const chRegistration = domainPrice('ch', IPurchaseDomainType.Registration);
  const chRenewal = domainPrice('ch', IPurchaseDomainType.Renewal);
  const total = (r: { firstYear: number; renewal: number }) => r.firstYear + (COMPARED_YEARS - 1) * r.renewal;
  const ours = chRegistration && chRenewal ? { name: 'YWT', url: undefined, firstYear: chRegistration.price, renewal: chRenewal.price } : undefined;
  const comparison = ours
    ? [ours, ...COMPETITORS]
        .map((r) => ({ ...r, total: total(r), moreThanUs: Math.round((total(r) / total(ours) - 1) * 100) }))
        // Cheapest first.
        .sort((a, b) => a.total - b.total)
    : [];

  const switchLink = (
    <Link className='underline' to='https://www.nic.ch' target='_blank'>
      SWITCH
    </Link>
  );

  const priceCell = (price?: IPrice) => {
    if (!price) return '–';
    if (price.price === 0) return <span className='text-green-600 font-medium'>{t('common.free')}</span>;
    return <PriceGadget price={price.price} currency={price.currency} />;
  };

  return (
    <div className='min-h-[80vh] flex flex-col w-full overflow-x-hidden'>
      <h1 className='font-mono text-center text-2xl md:text-4xl font-bold leading-tight tracking-tighter lg:leading-[1.1] mb-2 px-4'>
        {chRenewal ? t('domain.pricingHeadline', { price: PriceToString(chRenewal.price, chRenewal.currency) }) : t('domain.pricingTitle')}
      </h1>
      <p className='text-center text-sm text-muted-foreground mb-10 px-4 max-w-xl mx-auto'>{t('domain.pricingSubtitle')}</p>
      {/* What everyone pays. Restoring a deleted domain is a rare rescue, priced on the domain itself. */}
      <Table className='container max-w-3xl mx-auto text-xs sm:text-sm [&_td]:px-2 [&_th]:px-2 sm:[&_td]:px-4 sm:[&_th]:px-4'>
        <TableHeader>
          <TableRow>
            <TableHead>{t('domain.domainTld')}</TableHead>
            <TableHead>{t('domain.registration')}</TableHead>
            <TableHead>{t('domain.renewal')}</TableHead>
            <TableHead>{t('nav.transfer')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tlds.map((tld) => (
            <TableRow key={tld}>
              <TableCell className='font-medium'>.{tld}</TableCell>
              <TableCell>{priceCell(domainPrice(tld, IPurchaseDomainType.Registration))}</TableCell>
              <TableCell>{priceCell(domainPrice(tld, IPurchaseDomainType.Renewal))}</TableCell>
              <TableCell>{priceCell(domainPrice(tld, IPurchaseDomainType.Transfer))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <p className='text-center text-sm text-muted-foreground mt-4'>{t('domain.vatNote')}</p>

      <div className='flex justify-center mt-8'>
        <Button size='sm'>
          <Link to={ROUTES.domains.search}>{t('domain.findYourDomain')}</Link>
        </Button>
      </div>

      {/* Comparison with other Swiss registrars */}
      {ours && (
        <section className='container mx-auto px-2 sm:px-4 mt-24 max-w-3xl'>
          <h2 className='text-lg font-medium text-center mb-1'>{t('domain.compareTitle', { years: COMPARED_YEARS })}</h2>
          <p className='text-center text-sm text-muted-foreground mb-6'>{t('domain.compareSubtitle')}</p>
          <Table className='text-xs sm:text-sm [&_td]:px-2 [&_th]:px-2 sm:[&_td]:px-4 sm:[&_th]:px-4'>
            <TableHeader>
              <TableRow>
                <TableHead>{t('domain.compareRegistrar')}</TableHead>
                {/* Hidden on phones, where five columns don't fit: renewals and totals tell the story. */}
                <TableHead className='hidden sm:table-cell'>{t('domain.compareFirstYear')}</TableHead>
                <TableHead>{t('domain.compareRenewal')}</TableHead>
                <TableHead className='text-right'>{t('domain.compareTotal', { years: COMPARED_YEARS })}</TableHead>
                <TableHead className='text-right'>{t('domain.compareMoreThanUs')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.map((row) => (
                <TableRow key={row.name} className={cn(!row.url && 'bg-green-50 dark:bg-green-900/20 font-medium')}>
                  <TableCell>
                    {row.url ? (
                      <Link to={row.url} target='_blank' rel='nofollow noopener noreferrer' className='underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-current'>
                        {row.name}
                      </Link>
                    ) : (
                      row.name
                    )}
                  </TableCell>
                  <TableCell className='hidden sm:table-cell'>
                    <PriceGadget price={row.firstYear} currency='chf' />
                  </TableCell>
                  <TableCell>
                    <PriceGadget price={row.renewal} currency='chf' />
                  </TableCell>
                  <TableCell className='text-right'>
                    <PriceGadget price={row.total} currency='chf' className={cn(!row.url && 'text-green-700 dark:text-green-400')} />
                  </TableCell>
                  <TableCell className='text-right text-muted-foreground'>{row.url ? `+${row.moreThanUs}%` : '–'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter className='bg-transparent'>
              <TableRow className='hover:bg-transparent'>
                <TableCell colSpan={5} className='text-xs font-normal text-muted-foreground whitespace-normal'>
                  {t('domain.compareFootnote', { date: i18nDate(COMPETITORS_CHECKED_ON, 'long') })}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </section>
      )}

      {/* What's included section */}
      <section className='container mx-auto px-4 mt-24 max-w-3xl'>
        <h3 className='text-lg font-medium text-center mb-6 text-muted-foreground'>{t('domain.whatsIncludedDomain')}</h3>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6'>
          {[
            t('domain.whoisPrivacy'),
            t('domain.ywtDnsServers'),
            t('domain.dnssecSupportFeature'),
            t('domain.urlForwardingFeature'),
            t('domain.easyDnsManagement'),
            t('domain.greatUx'),
            t('domain.freeTransfers'),
            t('domain.emailSupport'),
            t('domain.noHiddenFees'),
          ].map((label) => (
            <div key={label} className='flex gap-2 items-center'>
              <div className='bg-green-100 dark:bg-green-900/30 p-1 rounded-full'>
                <Check className='w-3 h-3 text-green-600' />
              </div>
              <span className='text-sm'>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Spacer to push FAQ to bottom */}
      <div className='grow min-h-16' />

      <div className='container max-w-3xl mx-auto pb-8 px-4'>
        <h2 className='text-lg font-medium mb-4 text-center text-muted-foreground'>{t('common.faq')}</h2>
        <Accordion type='single' collapsible className='w-full grid md:grid-cols-2 gap-x-8'>
          <AccordionItem value='trust'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqTrustQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              <p>
                <Trans i18nKey='domain.faqTrustA' components={{ switch: switchLink }} />
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='vat'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqVatQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqVatA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='hidden-fees'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqHiddenFeesQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqHiddenFeesA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='renewal-price'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqRenewalPriceQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqRenewalPriceA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='domain-transfers'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqTransfersQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqTransfersA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='payment-methods'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqPaymentMethodsQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqPaymentMethodsA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='price-changes'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqPriceChangesQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              <p>
                <Trans i18nKey='domain.faqPriceChangesA' components={{ switch: switchLink }} />
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='refunds'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqRefundsQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              <p>
                <Trans i18nKey='domain.faqRefundsA' components={{ switch: switchLink }} />
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='more-than-one-year'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqMoreThanOneYearQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              <p>
                <Trans i18nKey='domain.faqMoreThanOneYearA' components={{ switch: switchLink }} />
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
